import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../data/translations';

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { language } = useLanguage();

  const heroSlides = [
    {
      id: 1,
      title: getTranslation(language, 'heroTitle1'),
      image: 'https://customer-assets.emergentagent.com/job_construction-global/artifacts/3c0en9nu_Capture%20d%E2%80%99%C3%A9cran%202026-04-23%20150456.png',
      link: '#'
    },
    {
      id: 2,
      title: getTranslation(language, 'heroTitle2'),
      image: 'https://customer-assets.emergentagent.com/job_construction-global/artifacts/y4svrwku_Capture%20d%E2%80%99%C3%A9cran%202026-04-23%20150531.png',
      link: '#'
    },
    {
      id: 3,
      title: getTranslation(language, 'heroTitle3'),
      image: 'https://customer-assets.emergentagent.com/job_construction-global/artifacts/f4p3dias_Capture%20d%E2%80%99%C3%A9cran%202026-04-23%20150554.png',
      link: '#'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  return (
    <div className="relative h-[420px] sm:h-[500px] md:h-[580px] lg:h-[600px] overflow-hidden" id="home">
      {/* Slides */}
      {heroSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/20 z-10" />
          <img
            src={slide.image}
            alt={slide.title}
            className="w-full h-full object-cover"
            style={slide.objectPosition ? { objectPosition: slide.objectPosition } : undefined}
          />
          <div className="absolute inset-0 z-20 flex items-center">
            <div className="max-w-[1800px] mx-auto px-4 sm:px-6 w-full">
              <div className="max-w-[90%] sm:max-w-xl md:max-w-2xl">
                <h1 className="text-white text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-snug sm:leading-tight">
                  {slide.title}
                </h1>
                <a
                  href={slide.link}
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-5 sm:px-8 py-2.5 sm:py-3 rounded font-semibold text-sm sm:text-base transition-all duration-300 transform hover:scale-105 border-2 border-orange-500"
                >
                  {getTranslation(language, 'readMore').toUpperCase()}
                  <ArrowRight size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 hover:bg-white/30 text-white p-2 sm:p-3 rounded-full backdrop-blur-sm transition-all duration-300"
        aria-label="Previous slide"
        data-testid="hero-prev-btn"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        onClick={goToNext}
        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 hover:bg-white/30 text-white p-2 sm:p-3 rounded-full backdrop-blur-sm transition-all duration-300"
        aria-label="Next slide"
        data-testid="hero-next-btn"
      >
        <ChevronRight size={20} />
      </button>

      {/* Dots Navigation */}
      <div className="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-2 sm:gap-3">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 rounded-full ${
              index === currentSlide
                ? 'bg-orange-500 w-8 sm:w-12 h-2.5 sm:h-3'
                : 'bg-white/50 hover:bg-white/75 w-2.5 sm:w-3 h-2.5 sm:h-3'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Slide Counter */}
      <div className="absolute bottom-4 sm:bottom-8 right-4 sm:right-8 z-30 text-white font-bold">
        <span className="text-3xl sm:text-5xl lg:text-6xl">{String(currentSlide + 1).padStart(2, '0')}</span>
        <span className="text-white/60 text-base sm:text-2xl"> / {String(heroSlides.length).padStart(2, '0')}</span>
      </div>
    </div>
  );
};

export default HeroSlider;